import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { History, Calendar, Building2, FileText, ChevronRight, RefreshCw, Search, Filter, Eye, FileImage } from 'lucide-react';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';
import TransactionsModal from './TransactionsModal';

const BankChatHistory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  // Modal state
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [transactionsData, setTransactionsData] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [autoOpenRunId, setAutoOpenRunId] = useState(searchParams.get('openRun'));

  // Fetch workflow runs
  const fetchWorkflowRuns = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = Cookies.get('sankalvax_token');
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.perPage.toString()
      });

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await fetch(`/api/bank/v2/workflows/executions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch workflow runs');
      }

      const data = await response.json();
      setWorkflowRuns(data.executions || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));

    } catch (err) {
      console.error('Error fetching workflow runs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions for a specific run
  const fetchTransactionsForRun = async (run) => {
    try {
      setLoadingTransactions(true);
      setSelectedRun(run);

      const token = Cookies.get('sankalvax_token');

      // Get results from bank_workflow_context table (same as CustomComponentRenderer)
      const response = await fetch(`/api/bank/v2/workflows/results/${run.run_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch transaction data');
      }

      const data = await response.json();
      console.log('Bank workflow results:', data);

      if (data.results && data.results.classifications) {
        // Extract transactions from the results
        const results = data.results;

        const transactionsData = {
          classifications: results.classifications || [],
          totallines: results.totallines || results.classifications?.length || 0,
          totaldebitcount: results.totaldebitcount || 0,
          totalcreditcount: results.totalcreditcount || 0,
          totaldebitamount: results.totaldebitamount || '0.00',
          totalcreditamount: results.totalcreditamount || '0.00'
        };

        // Store additional info for modal
        setSelectedRun({
          ...run,
          bank_name: data.bank_name || '',
          bank_account: data.bank_account || '',
          currency: data.currency || '',
          subsidiary: data.subsidiary || '',
          netsuite_account_id: data.netsuite_account_id || ''
        });

        setTransactionsData(transactionsData);
        setShowTransactionsModal(true);
      } else {
        throw new Error('No transaction data found for this run. The workflow may not have completed the classification step.');
      }

    } catch (err) {
      console.error('Error fetching transactions:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    fetchWorkflowRuns();
  }, [pagination.page, statusFilter]);

  // Auto-open TransactionsModal if openRun param is present
  useEffect(() => {
    if (autoOpenRunId && workflowRuns.length > 0 && !showTransactionsModal) {
      const runToOpen = workflowRuns.find(run => run.run_id === autoOpenRunId);
      if (runToOpen) {
        fetchTransactionsForRun(runToOpen);
        // Clear the URL param after opening
        setSearchParams({});
        setAutoOpenRunId(null);
      }
    }
  }, [autoOpenRunId, workflowRuns]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      running: 'bg-blue-100 text-blue-800 border-blue-200',
      waiting_for_input: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      pending: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${statusStyles[status] || statusStyles.pending}`}>
        {status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const filteredRuns = workflowRuns.filter(run => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      run.run_id?.toLowerCase().includes(query) ||
      run.workflow_name?.toLowerCase().includes(query) ||
      run.current_step?.toLowerCase().includes(query)
    );
  });

  const handleCloseModal = () => {
    setShowTransactionsModal(false);
    setSelectedRun(null);
    setTransactionsData(null);
  };

  return (
    <MainLayout title="Bank Chat History">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <History className="w-7 h-7 text-indigo-600" />
              Bank Reconciliation History
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              View and manage past bank statement reconciliations
            </p>
          </div>

          <button
            onClick={fetchWorkflowRuns}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by run ID, workflow name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="waiting_for_input">Waiting for Input</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Workflow Runs List */}
        {!loading && filteredRuns.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Bank Reconciliations Found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              Start a new bank reconciliation workflow to see history here.
            </p>
          </div>
        )}

        {!loading && filteredRuns.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Date / Run ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Child Runs
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRuns.map((run) => (
                    <tr
                      key={run.run_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDate(run.started_at)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {run.run_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(run.status)}
                      </td>
                      <td className="px-6 py-4">
                        {run.child_runs && run.child_runs.length > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs font-medium rounded-full">
                              {run.child_runs.length} transaction(s)
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No transactions</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {run.duration_seconds
                            ? `${Math.round(run.duration_seconds)}s`
                            : 'In progress'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={async () => {
                            try {
                              const token = Cookies.get('sankalvax_token');
                              const response = await fetch(`/api/bank/v2/workflows/view-file/${run.run_id}`, {
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              });

                              if (!response.ok) {
                                console.error('Failed to fetch file:', response.status);
                                alert('Failed to load file');
                                return;
                              }

                              const contentType = response.headers.get('content-type');
                              const blob = await response.blob();
                              const blobUrl = window.URL.createObjectURL(blob);

                              // For PDFs, open directly
                              if (contentType && contentType.includes('pdf')) {
                                window.open(blobUrl, '_blank');
                                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
                              } else {
                                // For Excel files, use Google Docs Viewer
                                const fileUrl = `${window.location.origin}/api/bank/v2/workflows/view-file/${run.run_id}?token=${token}`;
                                const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
                                window.open(viewerUrl, '_blank');
                                window.URL.revokeObjectURL(blobUrl);
                              }
                            } catch (error) {
                              console.error('Error viewing file:', error);
                              alert('Error loading file');
                            }
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 transition-colors"
                        >
                          <FileImage className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => fetchTransactionsForRun(run)}
                          disabled={loadingTransactions && selectedRun?.run_id === run.run_id}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                        >
                          {loadingTransactions && selectedRun?.run_id === run.run_id ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4" />
                              View Transactions
                              <ChevronRight className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > pagination.perPage && (
              <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((pagination.page - 1) * pagination.perPage) + 1} to{' '}
                  {Math.min(pagination.page * pagination.perPage, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.perPage >= pagination.total}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Transactions Modal */}
        {showTransactionsModal && transactionsData && (
          <TransactionsModal
            transactionsData={transactionsData}
            bankAccount={selectedRun?.bank_account || ''}
            bankName={selectedRun?.bank_name || 'Bank Statement'}
            currency={selectedRun?.currency || ''}
            subsidiary={selectedRun?.subsidiary || ''}
            runId={selectedRun?.run_id}
            workflowId={selectedRun?.workflow_id}
            netsuiteAccountId={selectedRun?.netsuite_account_id || ''}
            onClose={handleCloseModal}
            onRefresh={() => fetchTransactionsForRun(selectedRun)}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default BankChatHistory;
